// src/components/organization/UpgradeBanner.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { PLANS, STORAGE_KEYS } from '../../utils/constants';

const DISMISS_KEY = 'roadctrl_upgrade_banner_dismissed';

const UpgradeBanner = () => {
    const { subscription, effectivePlan } = useAuth();
    const [dismissed, setDismissed] = useState(
        () => sessionStorage.getItem(DISMISS_KEY) === 'true'
    );

    const isTrialing = subscription?.status === 'TRIALING';
    const trialDaysLeft = subscription?.trialEndsAt
        ? Math.max(0, Math.ceil((new Date(subscription.trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24)))
        : 0;

    // Mostra solo per FREE e STARTER (e TRIALING)
    const shouldShow = [PLANS.FREE, PLANS.STARTER].includes(effectivePlan) || isTrialing;

    if (!shouldShow || dismissed) return null;

    const handleDismiss = () => {
        sessionStorage.setItem(DISMISS_KEY, 'true');
        setDismissed(true);
    };

    const isStarter = effectivePlan === PLANS.STARTER;

    const config = isTrialing
        ? {
            gradient: 'from-orange-500 to-amber-500',
            title: `⏰ Trial in scadenza — ${trialDaysLeft} giorn${trialDaysLeft === 1 ? 'o' : 'i'} rimast${trialDaysLeft === 1 ? 'o' : 'i'}`,
            subtitle: 'Abbonati ora per non perdere le funzionalità PRO',
            cta: 'Abbonati ora →',
            textColor: 'text-orange-100',
            btnColor: 'text-orange-700',
        }
        : isStarter
            ? {
                gradient: 'from-blue-600 to-indigo-600',
                title: '🚀 Passa a PRO — Sblocca AI e progetti illimitati',
                subtitle: 'AI Assistant, report avanzati, utenti illimitati e supporto prioritario',
                cta: 'Scopri PRO →',
                textColor: 'text-blue-100',
                btnColor: 'text-blue-700',
            }
            : {
                gradient: 'from-purple-600 to-blue-600',
                title: '✨ Passa a STARTER o PRO — Sblocca tutto',
                subtitle: 'Più progetti, più utenti, AI Assistant e Report avanzati',
                cta: 'Vedi piani →',
                textColor: 'text-purple-100',
                btnColor: 'text-purple-700',
            };

    return (
        <div className={`relative rounded-2xl p-4 text-white overflow-hidden bg-gradient-to-r ${config.gradient}`}>
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-1/3 w-20 h-20 bg-white/5 rounded-full translate-y-1/2" />

            <div className="relative flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <SparklesIcon className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-sm">{config.title}</h3>
                        <p className={`text-xs mt-0.5 ${config.textColor}`}>{config.subtitle}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <Link to="/settings?tab=plan"
                        className={`px-4 py-1.5 bg-white ${config.btnColor} rounded-xl font-semibold text-sm hover:bg-gray-100 transition`}>
                        {config.cta}
                    </Link>
                    <button onClick={handleDismiss}
                        className="p-1.5 hover:bg-white/10 rounded-lg transition">
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UpgradeBanner;